import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import Layout from 'components/Form/PVQLocation/Layouts';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocation from 'components/Form/PVQLocation';
import { FinancialCivilJudgementItemModel } from 'forms/sections/financial/civilJudgement/types';
import NotApplicable from 'components/Form/NotApplicable';

interface Props extends Partial<FinancialCivilJudgementItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class CivilJudgementItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialCivilJudgementItemModel>) => {
    this.props.onUpdate({
      AmountOfJudgement: this.props.AmountOfJudgement,
      AmountOfJudgementEstimated: this.props.AmountOfJudgementEstimated,
      Why: this.props.Why,
      When: this.props.When,
      HasBeenPaid: this.props.HasBeenPaid,
      WhenPaid: this.props.WhenPaid,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      CourtName: this.props.CourtName,
      CourtLocation: this.props.CourtLocation,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      OtherParties: this.props.OtherParties,
      ...queue,
    });
  };

  updateAmountOfJudgement = (values: FinancialCivilJudgementItemModel['AmountOfJudgement']) => {
    this.update({
      AmountOfJudgement: values,
    });
  };

  updateAmountOfJudgementEstimated = (values: FinancialCivilJudgementItemModel['AmountOfJudgementEstimated']) => {
    this.update({
      AmountOfJudgementEstimated: values,
    });
  };

  updateWhy = (values: FinancialCivilJudgementItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateWhen = (values: FinancialCivilJudgementItemModel['When']) => {
    this.update({
      When: values,
    });
  };

  updateHasBeenPaid = (values: FinancialCivilJudgementItemModel['HasBeenPaid']) => {
    this.update({
      HasBeenPaid: values,
    });
  };

  updateWhenPaid = (values: FinancialCivilJudgementItemModel['WhenPaid']) => {
    this.update({
      WhenPaid: values,
    });
  };

  updateCourtName = (values: FinancialCivilJudgementItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtLocation = (values: FinancialCivilJudgementItemModel['CourtLocation']) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateOrderNum = (values: FinancialCivilJudgementItemModel['OrderNum']) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (values: FinancialCivilJudgementItemModel['OrderNumIdk']) => {
    this.update({
      OrderNumIdk: values,
    });
  };

  updateOtherParties = (values: FinancialCivilJudgementItemModel['OtherParties']) => {
    this.update({
      OtherParties: values,
    });
  };

  updateActionsTaken = (values: FinancialCivilJudgementItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenReason = (values: FinancialCivilJudgementItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateActionsTakenNone = (values: FinancialCivilJudgementItemModel['ActionsTakenNone']) => {
    this.update({
      ActionsTakenNone: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="civiljudgement-item">

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.amountTextEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountOfJudgement`}
        >
          <Currency
            maxLength="10"
            {...this.props.AmountOfJudgement}
            attribute={`${attribute}.AmountOfJudgement`}
            aria-label="Provide amount of judgement"
            name="amountofjudgement"
            onUpdate={this.updateAmountOfJudgement}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            name="amountofjudgement-estimated"
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.AmountOfJudgementEstimated}
            onUpdate={this.updateAmountOfJudgementEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AmountPastDueEstimated`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.whyTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Why`}
        >
          <Textarea
            {...this.props.Why}
            attribute={`${attribute}.Why`}
            name="why"
            onUpdate={this.updateWhy}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            onError={this.props.onError}
            hideDay
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={true}
          />
        </Field>

        <Branch
          {...this.props.HasBeenPaid}
          name="has_beenpaid"
          label={i18n.t('financial.pvq.civilJudgement.branch.ifYes.branch.title')}
          labelSize="h4"
          className="civiljudgement-current-branch"
          onUpdate={this.updateHasBeenPaid}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.HasBeenPaid`}
        />

        {this.props.HasBeenPaid?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.branch.ifYes.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenPaid`}
          >
            <DateControl
              {...this.props.WhenPaid}
              attribute={`${attribute}.WhenPaid`}
              name="whenpaid"
              onUpdate={this.updateWhenPaid}
              onError={this.props.onError}
              hideDay
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={true}
            />
          </Field>
        )}

        {this.props.HasBeenPaid?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.branch.ifNo.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTakenNone`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.HasBeenPaid?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.branch.ifNo.explainTextArea')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.courtNameText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            attribute={`${attribute}.CourtName`}
            name="courtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.courtLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtLocation`}
        >
          <PVQLocation
            {...this.props.CourtLocation}
            attribute={`${attribute}.CourtLocation`}
            name="courtlocation"
            layout={Layout.US_STREET_CITY_STATE_ZIP}
            onUpdate={this.updateCourtLocation}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.orderNumTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderNum`}
        >
          <NotApplicable
            {...this.props.OrderNumIdk}
            name="ordernumidk"
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateOrderNumIdk}
            onError={this.props.onError}
            attribute={`${attribute}.OrderNumIdk`}
          >
            <Text
              {...this.props.OrderNum}
              attribute={`${attribute}.OrderNum`}
              name="ordernum"
              onUpdate={this.updateOrderNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.civilJudgement.branch.ifYes.otherPartiesTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OtherParties`}
        >
          <Textarea
            {...this.props.OtherParties}
            attribute={`${attribute}.OtherParties`}
            name="otherparties"
            onUpdate={this.updateOtherParties}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
      </div>
    );
  }
}
CivilJudgementItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};
